import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';

import { ABROGATION_REASON_ID_BLOCK_ID } from 'KnmErknmPackage/MainModule/constants/knmErknmFieldsElementsIds';
import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import {
  getIsRsnCngPPXXII,
  getKnmFormRef,
  getKnmIsErknmStatusTypeCarriedOut,
  getKnmIsErknmStatusTypeWaitingCarryOut,
  getKnmIsNeedActSaveOrSign,
  getKnmWatchOnlyByTestUser,
} from 'KnmPackage/MainModule/selectors/knmSelectors';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SelectInputFieldAutoOptions } from 'UIPackage/MainModule/components/SelectInputFieldAutoOptions';
import { CommonCatalogsNamesEnum } from 'CorePackage/CatalogsModule/enums/CommonCatalogsNamesEnum';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';

import { Button } from '../../../../UIPackage/MainModule/components/Button';
import styles from './ErknmAbrogation.module.scss';
import { ErknmReasonsCancelledDocument } from '../../../ErknmReasonsModule/components/ErknmReasonsCancelledDocument';
import { IKnmAbrogationForm } from '../../../../APIPackage/KnmModule/interfaces/IKnmAbrogationForm';
import { ErknmReasonCancelledLink } from '../../../ErknmReasonsModule/components/ErknmReasonCancelledLink';
import { getUserRoleIsSupervisor } from '../../../../AuthPackage/MainModule/selectors/authSelectors';

interface IErknmAbrogationProps {
  abrogation: IKnmAbrogationForm | null | undefined;
}

interface IErknmAbrogationStoreProps {
  formRef: IKnmFormRef | undefined;
  isSupervisor: boolean;
  isKnmStatusTypeWaitingCarryOut: boolean;
  isKnmStatusTypeCarriedOut: boolean;
  isWatchOnlyByTestUser: boolean;
  isNeedActSaveOrSign?: boolean;
  isRsnCngPPXXII: boolean;
}

const ErknmAbrogationWithoutStore = ({
  isSupervisor,
  formRef,
  isKnmStatusTypeCarriedOut,
  isKnmStatusTypeWaitingCarryOut,
  abrogation,
  isWatchOnlyByTestUser,
  isNeedActSaveOrSign = false,
  isRsnCngPPXXII,
}: IErknmAbrogationProps & IErknmAbrogationStoreProps) => {
  /**
   * Функция очистки формы отмены мероприятия при изменении причины отмены
   */
  const handleAbrogationReasonSelectChange = useCallback(() => {
    formRef?.current.change('dataContent');
    formRef?.current.change('abrogation.reasonFile' as keyof IKnmForm);
    formRef?.current.change('abrogation.comment' as keyof IKnmForm);
    formRef?.current.change('abrogation.link' as keyof IKnmForm);
  }, [formRef]);

  /**
   * Функция добавления сведений об отмене
   */
  const addAbrogation = useCallback(() => {
    formRef?.current.change('abrogation', {});
  }, [formRef]);

  /**
   * Переменная для блокировки добавления/удаления "Сведений об отмене мероприятия"
   */
  const isDisabledAddRemoveAbrogation = useMemo(() => {
    return (
      !isSupervisor ||
      !!abrogation ||
      (!isKnmStatusTypeWaitingCarryOut && !isKnmStatusTypeCarriedOut) ||
      isWatchOnlyByTestUser ||
      isNeedActSaveOrSign ||
      isRsnCngPPXXII
    );
  }, [
    isSupervisor,
    abrogation,
    isKnmStatusTypeWaitingCarryOut,
    isKnmStatusTypeCarriedOut,
    isWatchOnlyByTestUser,
    isNeedActSaveOrSign,
  ]);

  return (
    <FieldBlock
      label="Сведения об отмене мероприятия"
      button={
        !abrogation && (
          <Button
            className="shared-btn-add"
            text="Добавить"
            type="button"
            variant="link"
            color="primary"
            onClick={addAbrogation}
            disabled={isDisabledAddRemoveAbrogation}
          />
        )
      }
      className={styles.FullWidth}
    >
      {abrogation && (
        <>
          <FieldBlock label="Основание для отмены" color="black" placementLabel="left">
            <SelectInputFieldAutoOptions
              id={ABROGATION_REASON_ID_BLOCK_ID}
              name="abrogation.reasonId"
              className={styles.FullWidth}
              disabled={
                (!isKnmStatusTypeWaitingCarryOut && !isKnmStatusTypeCarriedOut) ||
                isWatchOnlyByTestUser ||
                isNeedActSaveOrSign
              }
              optionName={CommonCatalogsNamesEnum.KNM_TYPES_CANCELED}
              onChange={handleAbrogationReasonSelectChange}
            />
            {!isDisabledAddRemoveAbrogation && (
              <CloseButton className={styles.RemoveAbrogation} onClick={() => formRef?.current.change('abrogation')} />
            )}
          </FieldBlock>
          <FieldBlock label="Комментарий" color="black" placementLabel="left" margin="top">
            <TextInputField
              id="abrogation.comment"
              name="abrogation.comment"
              className={styles.FullWidth}
              disabled={
                (!isKnmStatusTypeWaitingCarryOut && !isKnmStatusTypeCarriedOut) ||
                isWatchOnlyByTestUser ||
                isNeedActSaveOrSign
              }
              size="medium"
            />
          </FieldBlock>
          <FieldBlock margin="top">
            <ErknmReasonsCancelledDocument
              title={'Файл основания для отмены'}
              isWatchOnly={(!isKnmStatusTypeWaitingCarryOut && !isKnmStatusTypeCarriedOut) || isNeedActSaveOrSign}
              disableToAdd={
                (!isKnmStatusTypeWaitingCarryOut && !isKnmStatusTypeCarriedOut) ||
                isWatchOnlyByTestUser ||
                isNeedActSaveOrSign
              }
              className={styles.ReasonCancelKNMFile}
              name={`abrogation.reasonFile`}
              formRef={formRef}
            />
            {(abrogation?.reasonId?.id === 1 || abrogation?.reasonId?.id === 2) && (
              <ErknmReasonCancelledLink
                watchOnly={
                  (!isKnmStatusTypeWaitingCarryOut && !isKnmStatusTypeCarriedOut) ||
                  isWatchOnlyByTestUser ||
                  isNeedActSaveOrSign
                }
                abrogation={abrogation}
                isEnabled={true}
              />
            )}
          </FieldBlock>
        </>
      )}
    </FieldBlock>
  );
};

const mapStateToProps = (state: IRootState): IErknmAbrogationStoreProps => ({
  formRef: getKnmFormRef(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isKnmStatusTypeWaitingCarryOut: getKnmIsErknmStatusTypeWaitingCarryOut(state),
  isKnmStatusTypeCarriedOut: getKnmIsErknmStatusTypeCarriedOut(state),
  isWatchOnlyByTestUser: getKnmWatchOnlyByTestUser(state),
  isNeedActSaveOrSign: getKnmIsNeedActSaveOrSign(state),
  isRsnCngPPXXII: getIsRsnCngPPXXII(state),
});

export const ErknmAbrogation = connect(mapStateToProps)(ErknmAbrogationWithoutStore);
